/**
 * Per-locale primary SEO keywords for high-traffic public routes.
 *
 * Two purposes:
 *
 *   1. Runtime / smoke test: every translated `<title>` and
 *      `<meta description>` for a given route should contain the
 *      locale's primary keyword so titles remain aligned with local
 *      search intent rather than a literal English translation.
 *
 *   2. Translation prompt hint: the build-time `translate-dictionary`
 *      edge function receives this map so Gemini can produce
 *      CTR-optimized titles/descriptions ("lead with the primary
 *      keyword, include a benefit, add a soft CTA verb") rather than
 *      word-for-word rewrites.
 *
 * Routes are keyed by canonical root-relative path. English keywords
 * are the fallback for any locale that isn't explicitly listed.
 */

export type RouteKeywordMap = Record<string, string>;

/** Primary keyword *per locale* for each targeted route. */
export const SEO_KEYWORDS: Record<string, RouteKeywordMap> = {
  en: {
    "/": "football predictions",
    "/predictions": "football predictions",
    "/ai-predictions": "ai football predictions",
    "/high-odds": "high odds predictions",
    "/safe-bets": "safe bet predictions",
    "/analysis": "match analysis",
    "/match-preview": "match preview",
    "/performance": "prediction accuracy",
    "/track-record": "prediction track record",
    "/predictions/today": "today football predictions",
    "/leagues": "football league predictions",
    "/markets": "betting market predictions",
    "/markets/btts": "both teams to score predictions today", 
    "/competitions": "football competition predictions",
    "/countries": "football predictions by country",
    "/players": "football player stats",
    "/guides": "betting guides",
    "/faq": "football predictions faq",
    "/predictions/yesterday": "yesterday football predictions",
    "/predictions/tonight": "football tips tonight",
    "/predictions/jackpot": "jackpot predictions",
    "/predictions/mega-jackpot": "mega jackpot predictions",
    "/predictions/midweek-jackpot": "midweek jackpot predictions",
    "/predictions/accumulator-today": "football accumulator tips",
    "/predictions/premium-picks-today": "premium football picks",
    "/predictions/ht-ft-today": "ht ft predictions",
    "/predictions/double-chance-today": "double chance tips",
    "/predictions/draw-no-bet-today": "draw no bet tips",
    "/predictions/1x2-today": "1x2 predictions today",
    "/predictions/mathematical-today": "mathematical football predictions",
    "/predictions/gg-today": "gg predictions today",
    "/predictions/high-odds-today": "high odds predictions today",
    "/predictions/tennis-today": "tennis predictions today",
    "/predictions/ufc-today": "ufc predictions today",
    "/predictions/cricket-today": "cricket predictions today",
    "/predictions/horse-racing-today": "horse racing tips today",
  },
  es: {
    "/": "pronósticos de fútbol",
    "/predictions": "pronósticos de fútbol",
    "/ai-predictions": "pronósticos de fútbol con ia",
    "/high-odds": "cuotas altas",
    "/safe-bets": "apuestas seguras",
    "/analysis": "análisis de partidos",
    "/match-preview": "previa del partido",
    "/performance": "precisión de pronósticos",
    "/track-record": "historial de aciertos",
    "/predictions/today": "pronósticos de fútbol para hoy",
    "/leagues": "pronósticos por liga",
    "/markets": "mercados de apuestas",
    "/competitions": "pronósticos por competición",
    "/countries": "pronósticos por país",
    "/players": "estadísticas de jugadores",
    "/guides": "guías de apuestas",
    "/faq": "preguntas frecuentes pronósticos",
  },
  pt: {
    "/": "palpites de futebol",
    "/predictions": "palpites de futebol",
    "/ai-predictions": "palpites de futebol com ia",
    "/high-odds": "odds altas",
    "/safe-bets": "apostas seguras",
    "/analysis": "análise de jogos",
    "/match-preview": "prévia da partida",
    "/performance": "precisão dos palpites",
    "/track-record": "histórico de acertos",
    "/predictions/today": "palpites de futebol para hoje",
    "/leagues": "palpites por liga",
    "/markets": "mercados de apostas",
    "/competitions": "palpites por competição",
    "/countries": "palpites por país",
    "/players": "estatísticas de jogadores",
    "/guides": "guias de apostas",
    "/faq": "perguntas frequentes palpites",
  },
  fr: {
    "/": "pronostics football",
    "/predictions": "pronostics football",
    "/ai-predictions": "pronostics football ia",
    "/high-odds": "cotes élevées",
    "/safe-bets": "paris sûrs",
    "/analysis": "analyse de match",
    "/match-preview": "avant-match",
    "/performance": "précision des pronostics",
    "/track-record": "historique des pronostics",
    "/predictions/today": "pronostics football du jour",
    "/leagues": "pronostics par ligue",
    "/markets": "marchés de paris",
    "/competitions": "pronostics par compétition",
    "/countries": "pronostics par pays",
    "/players": "statistiques des joueurs",
    "/guides": "guides de paris",
    "/faq": "faq pronostics",
  },
  de: {
    "/": "fußball prognosen",
    "/predictions": "fußball prognosen",
    "/ai-predictions": "ki fußball prognosen",
    "/high-odds": "hohe quoten tipps",
    "/safe-bets": "sichere wetten",
    "/analysis": "spielanalyse",
    "/match-preview": "spielvorschau",
    "/performance": "prognose genauigkeit",
    "/track-record": "prognose bilanz",
    "/predictions/today": "fußball prognosen heute",
    "/leagues": "liga prognosen",
    "/markets": "wettmärkte",
    "/competitions": "wettbewerbs prognosen",
    "/countries": "prognosen nach land",
    "/players": "spielerstatistiken",
    "/guides": "wett anleitungen",
    "/faq": "prognosen faq",
  },
  it: {
    "/": "pronostici calcio",
    "/predictions": "pronostici calcio",
    "/ai-predictions": "pronostici calcio ia",
    "/high-odds": "quote alte",
    "/safe-bets": "scommesse sicure",
    "/analysis": "analisi partite",
    "/match-preview": "anteprima partita",
    "/performance": "precisione pronostici",
    "/track-record": "storico pronostici",
    "/predictions/today": "pronostici calcio oggi",
    "/leagues": "pronostici per lega",
    "/markets": "mercati scommesse",
    "/competitions": "pronostici per competizione",
    "/countries": "pronostici per paese",
    "/players": "statistiche giocatori",
    "/guides": "guide scommesse",
    "/faq": "faq pronostici",
  },
  ar: {
    "/": "توقعات كرة القدم",
    "/predictions": "توقعات كرة القدم",
    "/ai-predictions": "توقعات كرة القدم بالذكاء الاصطناعي",
    "/high-odds": "احتمالات عالية",
    "/safe-bets": "رهانات آمنة",
    "/analysis": "تحليل المباريات",
    "/match-preview": "استعراض المباراة",
    "/performance": "دقة التوقعات",
    "/track-record": "سجل التوقعات",
    "/predictions/today": "توقعات كرة القدم اليوم",
    "/leagues": "توقعات الدوريات",
    "/markets": "أسواق الرهان",
    "/competitions": "توقعات البطولات",
    "/countries": "توقعات حسب الدولة",
    "/players": "إحصائيات اللاعبين",
    "/guides": "أدلة الرهان",
    "/faq": "الأسئلة الشائعة",
  },
  ru: {
    "/": "прогнозы на футбол",
    "/predictions": "прогнозы на футбол",
    "/ai-predictions": "прогнозы на футбол ии",
    "/high-odds": "высокие коэффициенты",
    "/safe-bets": "надежные ставки",
    "/analysis": "анализ матчей",
    "/match-preview": "превью матча",
    "/performance": "точность прогнозов",
    "/track-record": "история прогнозов",
    "/predictions/today": "прогнозы на футбол сегодня",
    "/leagues": "прогнозы по лигам",
    "/markets": "рынки ставок",
    "/competitions": "прогнозы по турнирам",
    "/countries": "прогнозы по странам",
    "/players": "статистика игроков",
    "/guides": "гайды по ставкам",
    "/faq": "часто задаваемые вопросы",
  },
  ja: {
    "/": "サッカー予想",
    "/predictions": "サッカー予想",
    "/ai-predictions": "ai サッカー予想",
    "/high-odds": "高オッズ予想",
    "/safe-bets": "堅い予想",
    "/analysis": "試合分析",
    "/match-preview": "試合プレビュー",
    "/performance": "予想的中率",
    "/track-record": "予想実績",
    "/predictions/today": "今日のサッカー予想",
    "/leagues": "リーグ別予想",
    "/markets": "ベッティング市場",
    "/competitions": "大会別予想",
    "/countries": "国別予想",
    "/players": "選手データ",
    "/guides": "ベッティングガイド",
    "/faq": "よくある質問",
  },
  ko: {
    "/": "축구 예측",
    "/predictions": "축구 예측",
    "/ai-predictions": "ai 축구 예측",
    "/high-odds": "고배당 예측",
    "/safe-bets": "안전 베팅",
    "/analysis": "경기 분석",
    "/match-preview": "경기 프리뷰",
    "/performance": "예측 정확도",
    "/track-record": "예측 기록",
    "/predictions/today": "오늘의 축구 예측",
    "/leagues": "리그별 예측",
    "/markets": "베팅 마켓",
    "/competitions": "대회별 예측",
    "/countries": "국가별 예측",
    "/players": "선수 통계",
    "/guides": "베팅 가이드",
    "/faq": "자주 묻는 질문",
  },
  hi: {
    "/": "फुटबॉल भविष्यवाणी",
    "/predictions": "फुटबॉल भविष्यवाणी",
    "/ai-predictions": "एआई फुटबॉल भविष्यवाणी",
    "/high-odds": "हाई ऑड्स टिप्स",
    "/safe-bets": "सेफ बेट्स",
    "/analysis": "मैच विश्लेषण",
    "/match-preview": "मैच प्रीव्यू",
    "/performance": "भविष्यवाणी सटीकता",
    "/track-record": "ट्रैक रिकॉर्ड",
    "/predictions/today": "आज की फुटबॉल भविष्यवाणी",
    "/leagues": "लीग भविष्यवाणियाँ",
    "/markets": "बेटिंग मार्केट",
    "/competitions": "टूर्नामेंट भविष्यवाणियाँ",
    "/countries": "देश अनुसार भविष्यवाणी",
    "/players": "खिलाड़ी आँकड़े",
    "/guides": "बेटिंग गाइड",
    "/faq": "अक्सर पूछे जाने वाले प्रश्न",
  },
  tr: {
    "/": "futbol tahminleri",
    "/predictions": "futbol tahminleri",
    "/ai-predictions": "yapay zeka futbol tahminleri",
    "/high-odds": "yüksek oranlı tahminler",
    "/safe-bets": "güvenli bahisler",
    "/analysis": "maç analizi",
    "/match-preview": "maç önizleme",
    "/performance": "tahmin isabet oranı",
    "/track-record": "tahmin geçmişi",
    "/predictions/today": "günün futbol tahminleri",
    "/leagues": "lig tahminleri",
    "/markets": "bahis marketleri",
    "/competitions": "turnuva tahminleri",
    "/countries": "ülkelere göre tahminler",
    "/players": "oyuncu istatistikleri",
    "/guides": "bahis rehberleri",
    "/faq": "sıkça sorulan sorular",
  },
  id: {
    "/": "prediksi bola",
    "/predictions": "prediksi bola",
    "/ai-predictions": "prediksi bola ai",
    "/high-odds": "odds tinggi",
    "/safe-bets": "taruhan aman",
    "/analysis": "analisis pertandingan",
    "/match-preview": "pratinjau pertandingan",
    "/performance": "akurasi prediksi",
    "/track-record": "riwayat prediksi",
    "/predictions/today": "prediksi bola hari ini",
    "/leagues": "prediksi liga",
    "/markets": "pasar taruhan",
    "/competitions": "prediksi turnamen",
    "/countries": "prediksi per negara",
    "/players": "statistik pemain",
    "/guides": "panduan taruhan",
    "/faq": "pertanyaan umum",
  },
  pl: {
    "/": "typy piłkarskie",
    "/predictions": "typy piłkarskie",
    "/ai-predictions": "typy piłkarskie ai",
    "/high-odds": "wysokie kursy",
    "/safe-bets": "pewniaki",
    "/analysis": "analiza meczów",
    "/match-preview": "zapowiedź meczu",
    "/performance": "skuteczność typów",
    "/track-record": "historia typów",
    "/predictions/today": "typy piłkarskie na dziś",
    "/leagues": "typy według ligi",
    "/markets": "rynki bukmacherskie",
    "/competitions": "typy turniejowe",
    "/countries": "typy według kraju",
    "/players": "statystyki piłkarzy",
    "/guides": "poradniki bukmacherskie",
    "/faq": "najczęstsze pytania",
  },
  nl: {
    "/": "voetbal voorspellingen",
    "/predictions": "voetbal voorspellingen",
    "/ai-predictions": "ai voetbal voorspellingen",
    "/high-odds": "hoge odds tips",
    "/safe-bets": "veilige weddenschappen",
    "/analysis": "wedstrijdanalyse",
    "/match-preview": "wedstrijd preview",
    "/performance": "voorspel nauwkeurigheid",
    "/track-record": "voorspel historie",
    "/predictions/today": "voetbal voorspellingen vandaag",
    "/leagues": "competitie voorspellingen",
    "/markets": "wedmarkten",
    "/competitions": "toernooi voorspellingen",
    "/countries": "voorspellingen per land",
    "/players": "spelersstatistieken",
    "/guides": "wed gidsen",
    "/faq": "veelgestelde vragen",
  },
  el: {
    "/": "προγνωστικά ποδοσφαίρου",
    "/predictions": "προγνωστικά ποδοσφαίρου",
    "/ai-predictions": "προγνωστικά με ai",
    "/high-odds": "υψηλές αποδόσεις",
    "/safe-bets": "σίγουρα στοιχήματα",
    "/analysis": "ανάλυση αγώνων",
    "/match-preview": "προεπισκόπηση αγώνα",
    "/performance": "ακρίβεια προγνωστικών",
    "/track-record": "ιστορικό προγνωστικών",
    "/predictions/today": "προγνωστικά σήμερα",
    "/leagues": "προγνωστικά ανά λίγκα",
    "/markets": "αγορές στοιχήματος",
    "/competitions": "προγνωστικά διοργανώσεων",
    "/countries": "προγνωστικά ανά χώρα",
    "/players": "στατιστικά παικτών",
    "/guides": "οδηγοί στοιχήματος",
    "/faq": "συχνές ερωτήσεις",
  },
  sv: {
    "/": "fotbollstips",
    "/predictions": "fotbollstips",
    "/ai-predictions": "ai fotbollstips",
    "/high-odds": "höga odds tips",
    "/safe-bets": "säkra spel",
    "/analysis": "matchanalys",
    "/match-preview": "matchförhandsvisning",
    "/performance": "prognosnoggrannhet",
    "/track-record": "spelhistorik",
    "/predictions/today": "fotbollstips idag",
    "/leagues": "ligatips",
    "/markets": "spelmarknader",
    "/competitions": "turneringstips",
    "/countries": "tips per land",
    "/players": "spelarstatistik",
    "/guides": "spelguider",
    "/faq": "vanliga frågor",
  },
  fil: {
    "/": "football predictions",
    "/predictions": "football predictions",
    "/ai-predictions": "ai football predictions",
    "/high-odds": "high odds tips",
    "/safe-bets": "sure bets",
    "/analysis": "match analysis",
    "/match-preview": "match preview",
    "/performance": "prediction accuracy",
    "/track-record": "track record",
    "/predictions/today": "football predictions ngayon",
    "/leagues": "league predictions",
    "/markets": "betting markets",
    "/competitions": "competition predictions",
    "/countries": "predictions by country",
    "/players": "player stats",
    "/guides": "betting guides",
    "/faq": "mga madalas itanong",
  },
  am: {
    "/": "የእግር ኳስ ትንበያ",
    "/predictions": "የእግር ኳስ ትንበያ",
    "/ai-predictions": "ኤአይ የእግር ኳስ ትንበያ",
    "/high-odds": "ከፍተኛ ኦድስ",
    "/safe-bets": "ደህና ውርርድ",
    "/analysis": "የግጥሚያ ትንተና",
    "/match-preview": "የግጥሚያ ቅድመ እይታ",
    "/performance": "የትንበያ ትክክለኛነት",
    "/track-record": "የትንበያ ታሪክ",
    "/predictions/today": "የዛሬ የእግር ኳስ ትንበያ",
    "/leagues": "የሊግ ትንበያዎች",
    "/markets": "የውርርድ ገበያዎች",
    "/competitions": "የውድድር ትንበያዎች",
    "/countries": "በሀገር ደረጃ ትንበያዎች",
    "/players": "የተጫዋቾች ስታቲስቲክስ",
    "/guides": "የውርርድ መመሪያዎች",
    "/faq": "ተደጋጋሚ ጥያቄዎች",
  },
  zh: {
    "/": "足球预测",
    "/predictions": "足球预测",
    "/ai-predictions": "ai 足球预测",
    "/high-odds": "高赔率预测",
    "/safe-bets": "稳胆推荐",
    "/analysis": "比赛分析",
    "/match-preview": "赛前分析",
    "/performance": "预测准确率",
    "/track-record": "预测战绩",
    "/predictions/today": "今日足球预测",
    "/leagues": "联赛预测",
    "/markets": "投注市场",
    "/competitions": "赛事预测",
    "/countries": "各国足球预测",
    "/players": "球员数据",
    "/guides": "投注指南",
    "/faq": "常见问题",
  },
  "zh-TW": {
    "/": "足球預測",
    "/predictions": "足球預測",
    "/ai-predictions": "ai 足球預測",
    "/high-odds": "高賠率預測",
    "/safe-bets": "穩膽推薦",
    "/analysis": "比賽分析",
    "/match-preview": "賽前分析",
    "/performance": "預測準確率",
    "/track-record": "預測戰績",
    "/predictions/today": "今日足球預測",
    "/leagues": "聯賽預測",
    "/markets": "投注市場",
    "/competitions": "賽事預測",
    "/countries": "各國足球預測",
    "/players": "球員數據",
    "/guides": "投注指南",
    "/faq": "常見問題",
  },
};

/**
 * Regional sibling fallback map — locales that share vocabulary/market intent
 * closely enough that borrowing the sibling's primary keyword is better than
 * dropping to English. Only used when the locale itself has no entry AND no
 * DERIVED_KEYWORDS override.
 */
const SIBLING_FALLBACK: Record<string, string> = {
  pt: "es",
  nl: "de",
  sv: "de",
  fil: "id",
  am: "ar",
  el: "it",
};

/**
 * Auto-derived keywords produced by `scripts/derive-missing-keywords.ts`. Kept
 * separate from the human-curated `SEO_KEYWORDS` above so a build can
 * regenerate only the gaps without touching hand-tuned entries.
 */
export const DERIVED_KEYWORDS: Record<string, RouteKeywordMap> = {};

export interface KeywordResolution {
  keyword: string | undefined;
  /** "primary" (locale's own), "derived", "sibling:<code>", or "en". */
  source: string;
}

/**
 * Deep resolution with provenance — primary → derived → sibling → English.
 */
export function resolveKeyword(locale: string, route: string): KeywordResolution {
  const primary = SEO_KEYWORDS[locale]?.[route];
  if (primary) return { keyword: primary, source: "primary" };
  const derived = DERIVED_KEYWORDS[locale]?.[route];
  if (derived) return { keyword: derived, source: "derived" };
  const sibling = SIBLING_FALLBACK[locale];
  const siblingKw = sibling ? SEO_KEYWORDS[sibling]?.[route] : undefined;
  if (siblingKw) return { keyword: siblingKw, source: `sibling:${sibling}` };
  const english = SEO_KEYWORDS["en"]![route];
  if (english) return { keyword: english, source: "en" };
  const sportRoute = route.match(/^\/([a-z0-9-]+)-predictions(?:\/(today|tomorrow|weekend|fixtures|teams|markets))?/);
  if (sportRoute) {
    const sport = sportRoute[1]?.replace(/-/g, " ");
    const intent = sportRoute[2]?.replace(/-/g, " ");
    if (sport) {
      return {
        keyword: `${sport} predictions${intent ? ` ${intent}` : ""}`,
        source: "derived",
      };
    }
  }
  return { keyword: undefined, source: "en" };
}

/** Backwards-compatible resolver: returns just the keyword string. */
export function primaryKeyword(locale: string, route: string): string | undefined {
  return resolveKeyword(locale, route).keyword;
}

/** Case-insensitive substring match that survives non-Latin scripts. */
function includesLoose(haystack: string, needle: string): boolean {
  return haystack.toLowerCase().includes(needle.toLowerCase());
}

/**
 * If neither title nor description already carries the locale's primary
 * keyword, splice it in — lead the title with `${keyword} | ${title}`
 * (respecting the 60-char SERP cap) and append ` — ${keyword}.` to the
 * description (respecting the 160-char cap). Idempotent: passes when the
 * keyword is already present.
 */
export function enforceKeyword(
  title: string,
  description: string,
  keyword: string | undefined,
  { titleMax = 60, descMax = 160 }: { titleMax?: number; descMax?: number } = {},
): { title: string; description: string; injectedInto: "none" | "title" | "description" | "both" } {
  if (!keyword) return { title, description, injectedInto: "none" };
  const inTitle = title && includesLoose(title, keyword);
  const inDesc = description && includesLoose(description, keyword);
  if (inTitle || inDesc) return { title, description, injectedInto: "none" };

  let newTitle = title;
  let newDesc = description;
  let injected: "title" | "description" | "both" = "both";

  const proposedTitle = `${keyword} | ${title}`;
  if (proposedTitle.length <= titleMax) {
    newTitle = proposedTitle;
  } else {
    const trimmed = title.replace(/\s*[|·—–-]\s*Statlign\s*$/i, "");
    const alt = `${keyword} | ${trimmed}`;
    if (alt.length <= titleMax) newTitle = alt;
    else injected = "description";
  }

  const suffix = ` — ${keyword}.`;
  if ((description?.length ?? 0) + suffix.length <= descMax) {
    newDesc = `${description}${suffix}`.trim();
  } else if (description && description.length > suffix.length + 20) {
    newDesc = `${description.slice(0, descMax - suffix.length).trim()}${suffix}`;
  } else if (injected === "both") {
    injected = "title";
  }

  return { title: newTitle, description: newDesc, injectedInto: injected };
}

/* -----------------------------------------------------------------------------
 * Keyword clusters (site-wide expansion)
 *
 * The single-string `SEO_KEYWORDS` map above stays authoritative for the
 * primary keyword every existing caller depends on. This section layers a
 * richer cluster (secondary / longtail / related) on top so titles, meta
 * descriptions, JSON-LD, and the <meta name="keywords"> tag can target
 * multiple related queries per page without changing any existing behaviour.
 *
 * Backward compatibility: `primaryKeyword`, `resolveKeyword`, `enforceKeyword`
 * and `SEO_KEYWORDS` all keep their existing signatures. Cluster helpers are
 * additive.
 * -------------------------------------------------------------------------- */

export interface KeywordCluster {
  primary: string;
  secondary: string[];
  longtail: string[];
  related: string[];
}

export type RouteKeywordClusterMap = Record<string, KeywordCluster>;

/**
 * Locale-specific cluster overrides. Only English is authored in full — every
 * other locale inherits the English cluster's secondary/longtail/related terms
 * unless it overrides them here (the primary is always the locale's own
 * primary from SEO_KEYWORDS so localized SERP intent is preserved).
 */
const KEYWORD_CLUSTER_OVERRIDES: Record<string, RouteKeywordClusterMap> = {
  en: {
    "/": {
      primary: "football predictions",
      secondary: ["soccer predictions", "football tips", "betting tips"],
      longtail: [
        "today's football predictions",
        "best football predictions today",
        "accurate football predictions",
      ],
      related: ["match predictions", "football betting tips", "AI sports predictions"],
    },
    "/predictions": {
      primary: "football predictions",
      secondary: ["soccer predictions", "football tips", "daily predictions"],
      longtail: [
        "free football predictions",
        "daily football predictions",
        "football predictions for today's matches",
      ],
      related: ["match predictions", "betting tips", "sure predictions"],
    },
    "/ai-predictions": {
      primary: "ai football predictions",
      secondary: ["artificial intelligence football tips", "ai soccer predictions", "ai betting tips"],
      longtail: [
        "ai football predictions today",
        "best ai betting tips",
        "machine learning football predictions",
      ],
      related: ["machine learning predictions", "ai sports betting", "data-driven predictions"],
    },
    "/high-odds": {
      primary: "high odds predictions",
      secondary: ["high odds tips", "big odds football predictions", "value bets"],
      longtail: [
        "high odds football tips today",
        "best high odds bets",
        "value bet predictions today",
      ],
      related: ["value bets", "high return predictions", "underdog picks"],
    },
    "/safe-bets": {
      primary: "safe bet predictions",
      secondary: ["banker predictions", "confident betting picks"],
      longtail: ["safe bets today", "high confidence football tips"],
      related: ["low risk predictions", "banker tips"],
    },
    "/analysis": {
      primary: "match analysis",
      secondary: ["football match analysis", "pre-match analysis"],
      longtail: ["football match analysis today", "in-depth match analysis"],
      related: ["match preview", "statistical analysis"],
    },
    "/match-preview": {
      primary: "match preview",
      secondary: ["football match preview", "pre-match report"],
      longtail: ["today's match preview", "match preview and prediction"],
      related: ["team news", "head to head stats"],
    },
    "/performance": {
      primary: "prediction accuracy",
      secondary: ["tipster accuracy", "prediction success rate"],
      longtail: ["football prediction accuracy today", "verified tipster performance"],
      related: ["win rate", "prediction track record"],
    },
    "/track-record": {
      primary: "prediction track record",
      secondary: ["tipster history", "verified betting record"],
      longtail: ["football prediction track record", "verified tipster history"],
      related: ["past predictions", "success history"],
    },
    "/predictions/today": {
      primary: "today football predictions",
      secondary: ["football tips today", "daily football predictions"],
      longtail: [
        "free football predictions today",
        "accurate football tips today",
        "best football tips today",
      ],
      related: ["today's matches predictions", "daily betting tips"],
    },
    "/leagues": {
      primary: "football league predictions",
      secondary: ["league betting tips", "top league predictions"],
      longtail: ["premier league predictions today", "la liga betting tips"],
      related: ["league table predictions", "top scorer predictions"],
    },
    "/markets": {
      primary: "betting market predictions",
      secondary: ["football betting markets", "market predictions"],
      longtail: ["best football betting markets today", "high value betting markets"],
      related: ["over under tips", "BTTS predictions"],
    },
    "/competitions": {
      primary: "football competition predictions",
      secondary: ["cup predictions", "tournament tips"],
      longtail: ["champions league predictions today", "world cup predictions"],
      related: ["knockout predictions", "final predictions"],
    },
    "/countries": {
      primary: "football predictions by country",
      secondary: ["country league tips", "national football predictions"],
      longtail: ["football predictions by country today", "top country leagues predictions"],
      related: ["local football tips", "regional predictions"],
    },
    "/players": {
      primary: "football player stats",
      secondary: ["player performance stats", "top scorers"],
      longtail: ["football player stats today", "player prop bet stats"],
      related: ["player of the match", "player form"],
    },
    "/guides": {
      primary: "betting guides",
      secondary: ["how to bet on football", "betting strategy guides"],
      longtail: ["how to read football odds", "beginner football betting guide"],
      related: ["betting basics", "value betting"],
    },
    "/faq": {
      primary: "football predictions faq",
      secondary: ["betting faq", "prediction site faq"],
      longtail: ["how accurate are football predictions", "how to use prediction sites"],
      related: ["prediction help", "betting help"],
    },
  },
};

/** Resolution result carrying the full cluster + provenance. */
export interface KeywordClusterResolution {
  cluster: KeywordCluster | undefined;
  source: string;
}

/**
 * Full-cluster resolver. The primary keyword always comes from the locale's
 * own SEO_KEYWORDS (fallbacks apply as before). Secondary/longtail/related
 * come from KEYWORD_CLUSTER_OVERRIDES if authored for that locale, otherwise
 * from English (best-effort — many buyers still recognize English betting
 * terms, and mixing them in doesn't hurt localized SERPs).
 */
export function resolveKeywordCluster(
  locale: string,
  route: string,
): KeywordClusterResolution {
  const { keyword: primary, source } = resolveKeyword(locale, route);
  if (!primary) return { cluster: undefined, source };
  const localeOverride = KEYWORD_CLUSTER_OVERRIDES[locale]?.[route];
  const enOverride = KEYWORD_CLUSTER_OVERRIDES["en"]![route];
  const cluster: KeywordCluster = {
    primary,
    secondary: localeOverride?.secondary ?? enOverride?.secondary ?? [],
    longtail: localeOverride?.longtail ?? enOverride?.longtail ?? [],
    related: localeOverride?.related ?? enOverride?.related ?? [],
  };
  return { cluster, source };
}

/**
 * Flat, deduped, lower-cased list of every keyword in the cluster (primary
 * first). Used for `<meta name="keywords">`, JSON-LD `keywords`, and prompt
 * hints for translation tooling.
 */
export function getAllKeywords(locale: string, route: string): string[] {
  const { cluster } = resolveKeywordCluster(locale, route);
  if (!cluster) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const k of [cluster.primary, ...cluster.secondary, ...cluster.longtail, ...cluster.related]) {
    const key = k.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(k.trim());
  }
  return out;
}


